import type {
    DataTableColumn,
    FilterItemConfig,
    OptionItem,
} from '@/components/ui/data-table-customized'
import {
    createDateFormatter,
    createInternalLinkFormatter,
    createOptionsFormatter,
} from '@/components/ui/data-table-customized'
import { DEPARTMENT_IDS } from '@/constants/departments'
import { getEmployeesOptionsPaginated } from '@/modules/Employee/services'
import { getProductsGroupOptions } from '@/modules/ProductGroup/services'
import { TFunction } from 'i18next'
import type { Ticket } from '../types/response'
import {
    getTicketStatusColor,
    getTicketUrgencyColor,
    TicketMarjorable,
    TicketResponseLate,
    TicketSecurityTagOptions,
    TicketStatus,
    TicketTypeGroupOptions,
    TicketUrgency,
} from '../types/utils'

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Strip HTML tags and get plain text
 */
export const stripHtmlTags = (html: string): string => {
    if (!html) return ''

    if (typeof document !== 'undefined') {
        const tmp = document.createElement('div')
        tmp.innerHTML = html
        return tmp.textContent || tmp.innerText || ''
    }

    return html
        .replace(/<[^>]*>/g, '')
        .replace(/&nbsp;/g, ' ')
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .trim()
}

// ============================================================================
// Async Options Fetchers
// ============================================================================

// Department groups for ticket assignee filters
const TECH_DEPT_IDS = [
    DEPARTMENT_IDS.BOARD_OF_DIRECTORS,
    DEPARTMENT_IDS.TECHNOLOGY,
    DEPARTMENT_IDS.DEVELOPMENT,
]
const CS_DEPT_IDS = [
    DEPARTMENT_IDS.ACCOUNT,
    DEPARTMENT_IDS.SALES,
    DEPARTMENT_IDS.BOARD_OF_DIRECTORS,
]
const SALE_DEPT_IDS = [DEPARTMENT_IDS.SALES, DEPARTMENT_IDS.BOARD_OF_DIRECTORS]

// ============================================================================
// Status Options
// ============================================================================

const getTicketStatusOptions = (t: TFunction): OptionItem[] =>
    TicketStatus(t).map((item) => ({
        label: item.label,
        value: item.value,
        color: getTicketStatusColor(item.value) as OptionItem['color'],
    }))

const getTicketUrgencyOptions = (t: TFunction): OptionItem[] =>
    TicketUrgency(t).map((item) => ({
        label: item.label,
        value: item.value,
        color: getTicketUrgencyColor(item.value) as OptionItem['color'],
    }))

// ============================================================================
// Column Configuration
// ============================================================================

export const createTicketColumns = (
    t: TFunction
): DataTableColumn<Ticket>[] => [
    {
        key: 'ticket_id',
        title: 'NO.',
        width: 130,
        sortable: true,
        pinned: 'left',
        formatter: createInternalLinkFormatter({
            pathKey: 'ticketDetail',
            pathParams: (value) => ({ id: value }),
            label: (value) => `DC Ticket - ${value}`,
        }),
    },
    {
        key: 'employee_name',
        title: t('ticket-creator'),
        width: 200,
        sortable: true,
        render: (_, row) => row?.employee_name || row?.created_by_name || '-',
    },
    {
        key: 'ticket_status',
        title: t('ticket-status'),
        width: 160,
        sortable: true,
        formatter: createOptionsFormatter({
            options: getTicketStatusOptions(t),
        }),
    },
    {
        key: 'ticket_requested_at',
        title: t('customer-request'),
        width: 160,
        sortable: true,
        cellClassName: 'text-right',
        formatter: createDateFormatter({
            format: 'HH:mm DD/MM/YYYY',
        }),
    },
    {
        key: 'customer_name',
        title: t('customers'),
        width: 220,
        sortable: true,
        cellClassName: 'uppercase truncate',
        formatter: createInternalLinkFormatter({
            pathKey: 'customerDetail',
            pathParams: (_, row) => ({ id: row?.customer_id.toString() }),
            label: (_, row) =>
                [row?.customer_id, row?.company_name, row?.customer_name]
                    .filter(Boolean)
                    .join(' - '),
        }),
    },
    {
        key: 'ticket_type_group',
        title: t('ticket-type-group'),
        width: 250,
        cellClassName: 'truncate',
        render: (value, _) => {
            const group = TicketTypeGroupOptions(t).find(
                (g) => g.value === value
            )
            return group?.label || value || '-'
        },
    },
    {
        key: 'ticket_technical_assignees',
        title: t('ticket-technical-assignees'),
        width: 250,
        cellClassName: 'truncate',
        render: (_, row) => {
            const names = (row.ticket_technical_assignee_names ?? [])
                .map((a: { employee_name: string }) => a.employee_name)
                .join(', ')
            if (!names)
                return (
                    <span className="text-gray-400 italic">
                        {t('unassigned')}
                    </span>
                )
            return names
        },
    },
    {
        key: 'ticket_cs_assignees',
        title: t('ticket-cs-assignees'),
        width: 250,
        cellClassName: 'truncate',
        render: (_, row) => {
            const names = (row.ticket_cs_assignee_names ?? [])
                .map((a: { employee_name: string }) => a.employee_name)
                .join(', ')
            if (!names)
                return (
                    <span className="text-gray-400 italic">
                        {t('unassigned')}
                    </span>
                )
            return names
        },
    },
    {
        key: 'ticket_sale_assignees',
        title: t('ticket-sale-assignees'),
        width: 250,
        render: (_, row) => {
            const names = (row.ticket_sale_assignee_names ?? [])
                .map((a: { employee_name: string }) => a.employee_name)
                .join(', ')
            if (!names)
                return (
                    <span className="text-gray-400 italic">
                        {t('unassigned')}
                    </span>
                )
            return names
        },
    },
    {
        key: 'product_group_name',
        title: t('product_group_name'),
        width: 300,
        sortable: true,
    },
    {
        key: 'ticket_request_id',
        title: t('ticket-request-title'),
        width: 200,
        render: (_, row) => row?.ticket_request_title || '-',
    },
    {
        key: 'ticket_type_id',
        title: t('ticket-type'),
        width: 300,
        cellClassName: 'truncate',
        render: (_, row) => row?.ticket_type_title || '-',
    },
    {
        key: 'ticket_priority',
        title: t('ticket-urgency'),
        width: 140,
        sortable: true,
        formatter: createOptionsFormatter({
            options: getTicketUrgencyOptions(t),
        }),
    },
    {
        key: 'ticket_content',
        title: t('ticket-content'),
        width: 350,
        cellClassName: 'truncate',
        render: (value) => stripHtmlTags(value || ''),
    },
    {
        key: 'ticket_estimated_completion_date',
        title: t('ticket-estimated-completion-date'),
        width: 160,
        cellClassName: 'text-right',
        formatter: createDateFormatter({
            format: 'HH:mm DD/MM/YYYY',
        }),
    },
    {
        key: 'ticket_time_release',
        title: t('ticket-date-completed'),
        width: 160,
        cellClassName: 'text-right',
        formatter: createDateFormatter({
            format: 'HH:mm DD/MM/YYYY',
        }),
    },
    {
        key: 'ticket_time_repply',
        title: t('ticket-time-repply'),
        width: 160,
        cellClassName: 'text-right',
        formatter: createDateFormatter({
            format: 'HH:mm DD/MM/YYYY',
        }),
    },
    {
        key: 'created_at',
        title: t('created-at'),
        width: 160,
        cellClassName: 'text-right',
        formatter: createDateFormatter({
            format: 'HH:mm DD/MM/YYYY',
        }),
    },
]

// ============================================================================
// Filter Configuration (Using NEW FilterBar types)
// ============================================================================

export const createTicketFilters = (t: TFunction): FilterItemConfig[] => [
    {
        name: 'ticket_status',
        type: 'multi-select',
        label: t('ticket-status-filters'),
        options: TicketStatus(t),
        placeholder: t('select-status'),
    },
    {
        name: 'ticket_priority',
        type: 'multi-select',
        label: t('ticket-priority-filters'),
        options: TicketUrgency(t),
        placeholder: t('select-priority'),
    },
    {
        name: 'ticket_type_group',
        type: 'multi-select',
        label: t('ticket-type-group-label'),
        options: TicketTypeGroupOptions(t),
        placeholder: t('select-type-group'),
    },
    {
        name: 'ticket_technical_assignees',
        type: 'async-select',
        label: t('ticket-technical-assignees'),
        asyncOptions: async (query, page) => {
            const result = await getEmployeesOptionsPaginated(
                query,
                TECH_DEPT_IDS,
                undefined,
                page
            )
            if (page === 1 && !query) {
                return {
                    ...result,
                    options: [
                        { label: t('no-tech-assignee'), value: '-1' },
                        ...result.options,
                    ],
                }
            }
            return result
        },
        multiple: true,
        placeholder: t('select-assignees'),
    },
    {
        name: 'ticket_cs_assignees',
        type: 'async-select',
        label: t('ticket-cs-assignees'),
        asyncOptions: async (query, page) => {
            return getEmployeesOptionsPaginated(
                query,
                CS_DEPT_IDS,
                undefined,
                page
            )
        },
        multiple: true,
        placeholder: t('select-assignees'),
    },
    {
        name: 'ticket_sale_assignees',
        type: 'async-select',
        label: t('ticket-sale-assignees'),
        asyncOptions: async (query, page) => {
            return getEmployeesOptionsPaginated(
                query,
                SALE_DEPT_IDS,
                undefined,
                page
            )
        },
        multiple: true,
        placeholder: t('select-assignees'),
    },
    {
        name: 'ticket_response_late',
        type: 'select',
        label: t('ticket-late'),
        options: TicketResponseLate(t),
        placeholder: t('select-late-status'),
    },
    {
        name: 'ticket_marjorable',
        type: 'multi-select',
        label: t('major-incident'),
        options: TicketMarjorable().map((item) => ({
            label: item.label,
            value: item.value.toString(),
        })),
        placeholder: t('select-major'),
    },
    {
        name: 'product_group_name',
        type: 'async-select',
        label: t('service-group'),
        asyncOptions: getProductsGroupOptions,
        multiple: true,
        placeholder: t('select-product-group'),
    },
    {
        name: 'ticket_security_tag',
        type: 'multi-select',
        label: 'Security Tag',
        options: TicketSecurityTagOptions(),
        placeholder: 'Select security tag',
    },
]

// ============================================================================
// Constants
// ============================================================================

export const TICKET_ALLOWED_SORT_FIELDS = [
    'ticket_id',
    'ticket_status',
    'employee_name',
    'ticket_priority',
    'ticket_requested_at',
    'created_at',
]

export interface TicketFilters {
    ticket_status: string[]
    ticket_priority: string[]
    ticket_type_group: string[]
    ticket_marjorable: string[]
    product_group_name: string[]
    ticket_technical_assignees: string[]
    ticket_cs_assignees: string[]
    ticket_sale_assignees: string[]
    ticket_response_late: string
    ticket_security_tag: string[]
}

export const TICKET_FILTER_DEFAULTS: TicketFilters = {
    ticket_status: [],
    ticket_priority: [],
    ticket_type_group: [],
    ticket_marjorable: [],
    product_group_name: [],
    ticket_technical_assignees: [],
    ticket_cs_assignees: [],
    ticket_sale_assignees: [],
    ticket_response_late: '',
    ticket_security_tag: [],
}
